import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { products, contactDetails, type Product } from "@/mocks/products";

type ProductPageProps = {
  slug: string;
};

function RelatedProducts({ currentSlug }: { currentSlug: string }) {
  const related = products.filter((item) => item.slug !== currentSlug).slice(0, 3);
  return (
    <section className="w-full bg-background-100 border-t border-background-200">
      <div className="w-full px-4 md:px-8 py-14 md:py-16">
        <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950">
          Weitere Produktgruppen
        </h2>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {related.map((item) => (
            <Link
              key={item.slug}
              to={`/${item.slug}`}
              className="group flex flex-col rounded-lg bg-background-50 border border-background-200 p-5 md:p-6 hover:border-primary-300 transition-colors"
            >
              <div className="w-full h-32 flex items-center justify-center bg-background-100 rounded-md">
                <img
                  src={item.image}
                  alt={`${item.name} – Franz Drexelius Metallverarbeitung`}
                  title={`${item.name} | Franz Drexelius Metallverarbeitung GmbH`}
                  className="max-w-[80%] max-h-[75%] object-contain"
                />
              </div>
              <h3 className="mt-5 font-heading font-semibold text-lg text-foreground-950">
                {item.name}
              </h3>
              <span className="mt-4 inline-flex items-center gap-2 font-label text-xs uppercase tracking-wide text-primary-600 group-hover:gap-3 transition-all">
                mehr lesen
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductDetail({ product }: { product: Product }) {
  return (
    <SiteLayout>
      <div className="w-full bg-background-100 border-b border-background-200">
        <nav aria-label="Brotkrumen" className="w-full px-4 md:px-8 py-4">
          <ol className="flex flex-wrap items-center gap-2 font-label text-xs text-foreground-500">
            <li>
              <Link to="/" className="hover:text-primary-600 transition-colors">
                Start
              </Link>
            </li>
            <li aria-hidden="true">
              <i className="ri-arrow-right-s-line" />
            </li>
            <li className="text-foreground-800">{product.name}</li>
          </ol>
        </nav>
      </div>

      <section className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="animate-rise order-2 lg:order-1">
            <p className="font-label text-[11px] uppercase tracking-[0.18em] text-primary-600">
              Produktgruppe
            </p>
            <h1 className="mt-3 font-heading font-semibold text-3xl md:text-5xl text-foreground-950">
              {product.title}
            </h1>
            <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
              {product.teaser}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-primary-600 text-background-50 font-label text-sm uppercase tracking-wide whitespace-nowrap hover:bg-primary-500 transition-colors"
              >
                <i className="ri-mail-send-line text-base" aria-hidden="true" />
                Anfrage senden
              </Link>
              <a
                href={contactDetails.phoneHref}
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md border border-foreground-300 text-foreground-900 font-label text-sm uppercase tracking-wide whitespace-nowrap hover:bg-background-100 transition-colors"
              >
                <i className="ri-phone-line text-base" aria-hidden="true" />
                {contactDetails.phone}
              </a>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <div className="w-full rounded-lg bg-background-100 border border-background-200 p-8 md:p-12">
              <div className="w-full h-56 md:h-72 flex items-center justify-center">
                <img
                  src={product.image}
                  alt={`${product.name} – Franz Drexelius Metallverarbeitung`}
                  title={`${product.name} | Franz Drexelius Metallverarbeitung GmbH`}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 border-y border-background-200">
        <div className="w-full px-4 md:px-8 py-14 md:py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950">
              Beschreibung
            </h2>
            <p className="mt-5 text-sm md:text-base text-foreground-700 leading-relaxed">
              {product.description}
            </p>

            <h3 className="mt-9 font-heading font-semibold text-lg text-foreground-950">
              Ihre Vorteile
            </h3>
            <ul className="mt-4 flex flex-col gap-3">
              {product.features.map((feature) => (
                <li key={feature} className="flex items-start gap-3">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-accent-100 text-accent-800 flex items-center justify-center shrink-0">
                    <i className="ri-check-line text-xs" aria-hidden="true" />
                  </span>
                  <span className="text-sm md:text-base text-foreground-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950">
              Technische Angaben
            </h2>
            <dl className="mt-5 rounded-lg bg-background-50 border border-background-200 divide-y divide-background-200">
              {product.variants.map((variant) => (
                <div
                  key={variant.label}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 px-5 py-4"
                >
                  <dt className="font-label text-xs uppercase tracking-wide text-foreground-500">
                    {variant.label}
                  </dt>
                  <dd className="text-sm text-foreground-900">{variant.value}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-5 text-xs text-foreground-500 leading-relaxed">
              Alle Angaben sind Richtwerte. Abmessungen und Werkstoffe fertigen wir nach Ihrer
              Zeichnung oder Ihrem Muster. Fertigungslose ab 300 Stück.
            </p>
          </div>
        </div>
      </section>

      <RelatedProducts currentSlug={product.slug} />
    </SiteLayout>
  );
}

export default function ProductPage({ slug }: ProductPageProps) {
  const product = products.find((item) => item.slug === slug);

  if (!product) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-8 py-20 text-center">
          <h1 className="font-heading font-semibold text-2xl text-foreground-950">
            Produkt nicht gefunden
          </h1>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-primary-600 text-background-50 font-label text-sm uppercase tracking-wide"
          >
            Zur Startseite
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return <ProductDetail product={product} />;
}